// WO-35: pure helpers for components/PriceChart.js (unit-tested in priceChartData.test.js).

const DAY_MS = 86400000;

// Two prices of one series more than this many days apart are not joined: the line breaks there, so a period with no
// data (e.g. 2026-09-08..10-15, when the TCGplayer archive was offline) is never drawn as if prices were observed.
export const GAP_DAYS = 21;

// Ranges the chart steps up to when the opening range has fewer than two prices (a single dot is not a history).
export const AUTO_WIDEN_STEPS = [365, 3650];

export const toTs = (date) => {
  const t = new Date(date).getTime();
  return Number.isFinite(t) ? t : null;
};

// Merge the series of every source into chart rows keyed by timestamp. Each source is split into segments at gaps
// longer than GAP_DAYS; every segment gets its own data key (`price_<src>__<n>`) so recharts can join a series across
// dates where only other sources have prices (connectNulls) without bridging a real gap.
export function buildChartRows(seriesBySource, sources, getPrice) {
  const rows = new Map();
  const segments = [];
  let hasGap = false;
  sources.forEach((src) => {
    const points = (seriesBySource[src]?.data_points || [])
      .map((p) => ({ p, ts: toTs(p.date), price: getPrice(p) }))
      .filter((x) => x.ts !== null && x.price !== null)
      .sort((a, b) => a.ts - b.ts);
    let seg = -1;
    let prevTs = null;
    points.forEach(({ p, ts, price }) => {
      if (prevTs === null || ts - prevTs > GAP_DAYS * DAY_MS) {
        if (prevTs !== null) hasGap = true;
        seg += 1;
        segments.push({ src, key: `price_${src}__${seg}`, count: 0 });
      }
      prevTs = ts;
      segments[segments.length - 1].count += 1;
      const row = rows.get(ts) || { ts, date: p.date };
      row[`price_${src}__${seg}`] = price;
      row[`price_${src}`] = price;
      row[`listing_count_${src}`] = p.listing_count;
      row[`min_price_${src}`] = Number.isFinite(Number(p.min_price)) && p.min_price !== null ? Number(p.min_price) : null;
      row[`max_price_${src}`] = Number.isFinite(Number(p.max_price)) && p.max_price !== null ? Number(p.max_price) : null;
      rows.set(ts, row);
    });
  });
  return { rows: [...rows.values()].sort((a, b) => a.ts - b.ts), segments, hasGap };
}

// Y axis range: a little room above and below, never below zero (the old 'dataMin - 1' showed -$0.82 on a $0.18
// card), and a visible band when every price is the same.
export function yDomain(values) {
  const v = values.filter((x) => Number.isFinite(x));
  if (!v.length) return [0, 1];
  const lo = Math.min(...v);
  const hi = Math.max(...v);
  const pad = Math.max((hi - lo) * 0.1, Math.abs(hi) * 0.02, 0.01);
  return [Math.max(0, lo - pad), hi + pad];
}

export const maxPointCount = (seriesBySource) => Object.values(seriesBySource || {})
  .reduce((m, d) => Math.max(m, Array.isArray(d?.data_points) ? d.data_points.length : 0), 0);

// The next range to try when the opening range shows fewer than two prices, or null to stay.
export function nextAutoWidenPeriod(currentDays, seriesBySource) {
  if (maxPointCount(seriesBySource) >= 2) return null;
  return AUTO_WIDEN_STEPS.find((d) => d > currentDays) || null;
}

// First and last date with a price in any shown series (for the "Prices from ... to ..." caption).
export function dataSpan(rows) {
  if (!rows.length) return null;
  return { first: rows[0].date, last: rows[rows.length - 1].date };
}
